import { ClerkProvider } from '@clerk/clerk-react'
import type { ReactNode } from 'react'
import {
  origensClerkAppearance,
  type OrigensClerkAppearance,
  type OrigensColorScheme,
} from './appearance.js'

export interface OrigensAuthProviderProps {
  /**
   * Clerk publishable key for the current environment. Callers read this from
   * `import.meta.env.VITE_CLERK_PUBLISHABLE_KEY` (or an equivalent
   * server-provided value) since Vite only exposes `VITE_`-prefixed
   * variables to browser code.
   */
  publishableKey: string
  /**
   * Where Clerk sends a signed-out user by default. Individual sign-out
   * flows may still pass their own redirect target.
   */
  afterSignOutUrl?: string
  /**
   * Resolved light/dark from ThemeProvider (not the raw preference).
   * When omitted, Clerk uses its default light chrome.
   */
  colorScheme?: OrigensColorScheme
  /**
   * Optional appearance override. When set, replaces the default
   * {@link origensClerkAppearance} for this provider (component-level
   * `appearance` props still merge on top via Clerk).
   */
  appearance?: OrigensClerkAppearance
  children: ReactNode
}

/**
 * Thin wrapper around Clerk's React provider. This component renders browser
 * APIs (storage, network, context) and must be mounted only inside a
 * `ClientOnly` boundary from `@go-beyond/react` so the GoBeyond portable
 * compiler never has to render it on the server.
 */
export function OrigensAuthProvider({
  publishableKey,
  afterSignOutUrl = '/sign-in',
  colorScheme = 'light',
  appearance,
  children,
}: OrigensAuthProviderProps) {
  return (
    <ClerkProvider
      publishableKey={publishableKey}
      afterSignOutUrl={afterSignOutUrl}
      appearance={appearance ?? origensClerkAppearance(colorScheme)}
    >
      {children}
    </ClerkProvider>
  )
}
